import { paths } from 'apiTyped/types';
import {
  UseQueryOptions,
  UseQueryResult,
  keepPreviousData,
  useQuery
} from '@tanstack/react-query';
import { FetchOptions, FetchResponse } from 'openapi-fetch';
import type {
  FilterKeys,
  PathsWithMethod,
  HasRequiredKeys,
  HttpMethod
} from 'openapi-typescript-helpers';
import getClient from 'apiTyped/client';
import { getTypedQueryKey } from 'apiTyped/helpers/getTypedQueryKey';

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'detail' in error) {
    const detail = (error as { detail?: unknown }).detail;
    if (typeof detail === 'string') return detail;
  }
  return 'Request failed';
};

type QueryOptions<T> = {
  queryOpts?: Partial<
    Omit<
      UseQueryOptions<FetchResponse<T>['data'], FetchResponse<T>['error']>,
      'queryFn' | 'queryKey'
    >
  >;
};

// eslint-disable-next-line @typescript-eslint/ban-types
type CreateUseTypedQuery<Paths extends {}> = {
  useTypedQuery<T extends HttpMethod, P extends PathsWithMethod<Paths, T>>(
    _method: T,
    _url: P,
    ..._options: HasRequiredKeys<
      FetchOptions<FilterKeys<Paths[P], T>>
    > extends never
      ? [
          (
            | (FetchOptions<FilterKeys<Paths[P], T>> &
                QueryOptions<T extends keyof Paths[P] ? Paths[P][T] : unknown>)
            | undefined
          )?
        ]
      : [
          FetchOptions<FilterKeys<Paths[P], T>> &
            QueryOptions<T extends keyof Paths[P] ? Paths[P][T] : unknown>
        ]
  ): UseQueryResult<
    FetchResponse<T extends keyof Paths[P] ? Paths[P][T] : unknown>['data'],
    FetchResponse<T extends keyof Paths[P] ? Paths[P][T] : unknown>['error']
  >;
};

export const createUseTypedQuery: CreateUseTypedQuery<paths> = {
  // @ts-expect-error It does return the correct type
  useTypedQuery(method, url, ...options) {
    const option = options[0];

    return useQuery({
      // @ts-expect-error url is ok
      queryKey: getTypedQueryKey(url, option),
      queryFn: async ({ signal }) => {
        const client = await getClient();
        // @ts-expect-error All good, we know this method exists
        const { data, error } = await client[method.toUpperCase()](url, {
          ...option,
          signal
        });
        if (error) throw new Error(getErrorMessage(error));
        return data;
      },
      // Keeps the previous page's data visible while a new query key is
      // fetching (e.g. filters/status chip changed), instead of resetting
      // to `undefined` and flashing default/zero values — callers can
      // still opt out via their own `queryOpts.placeholderData`.
      // @ts-expect-error placeholderData type is ok
      placeholderData: keepPreviousData,
      ...option?.queryOpts
    });
  }
};

export const useTypedQuery = createUseTypedQuery.useTypedQuery;
